import { useSyncExternalStore } from 'react';
import { IPriceData } from 'Quotes/types/store';
import { LocalStorageKeys } from 'User/types/localStorageTypes';

const PRICE_DISPLAY_STORAGE_KEY = LocalStorageKeys.QUOTE_PRICE_DISPLAY_MONTHLY;
const preferenceListeners = new Set<() => void>();

const subscribeToMonthlyDisplayPreference = (listener: () => void) => {
  preferenceListeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === PRICE_DISPLAY_STORAGE_KEY || event.key === null) listener();
  };

  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);

  return () => {
    preferenceListeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
};

export const getMonthlyDisplayPreference = (): boolean => {
  if (typeof localStorage === 'undefined') return true;

  const preference = localStorage.getItem(PRICE_DISPLAY_STORAGE_KEY);
  return preference === null ? true : preference === 'true';
};

export const setMonthlyDisplayPreference = (viewAsMonthly: boolean): void => {
  if (typeof localStorage === 'undefined') return;

  localStorage.setItem(PRICE_DISPLAY_STORAGE_KEY, String(viewAsMonthly));
  preferenceListeners.forEach((listener) => listener());
};

export const useMonthlyDisplayPreference = (): boolean =>
  useSyncExternalStore(subscribeToMonthlyDisplayPreference, getMonthlyDisplayPreference, () => true);

export const toMonthlyAmount = (annualAmount: number): number => annualAmount / 12;

export const displayAnnualAmount = (
  annualAmount: number | null | undefined,
  viewAsMonthly: boolean
): number | null | undefined => {
  if (annualAmount === null || annualAmount === undefined || !viewAsMonthly) return annualAmount;
  return toMonthlyAmount(annualAmount);
};

export const priceDataForDisplay = (priceData: IPriceData, viewAsMonthly: boolean): IPriceData => {
  if (!viewAsMonthly) return priceData;

  return {
    ...priceData,
    a_end_annual_cost:
      priceData.a_end_annual_cost === null ? null : toMonthlyAmount(priceData.a_end_annual_cost),
    b_end_annual_cost:
      priceData.b_end_annual_cost === null ? null : toMonthlyAmount(priceData.b_end_annual_cost),
    amortised_annual_price:
      priceData.amortised_annual_price === null ? null : toMonthlyAmount(priceData.amortised_annual_price),
    annual_ip_charge: priceData.annual_ip_charge === null ? null : toMonthlyAmount(priceData.annual_ip_charge),
    cloud_connect: priceData.cloud_connect
      ? {
          ...priceData.cloud_connect,
          annual_cost:
            priceData.cloud_connect.annual_cost === null ? null : toMonthlyAmount(priceData.cloud_connect.annual_cost),
          annual_price: displayAnnualAmount(priceData.cloud_connect.annual_price, true),
        }
      : priceData.cloud_connect,
  };
};
